import { createFileRoute } from "@tanstack/react-router";
import { Page, PageHead } from "@/components/site/Page";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of use | CLOSDD" },
      {
        name: "description",
        content: "The rules, kept short. Read the terms for using CLOSDD.",
      },
    ],
  }),
  component: Terms,
});

const sections = [
  ["the-basics", "The basics"],
  ["salespeople", "Salespeople"],
  ["employers", "Employers"],
  ["prices-and-refunds", "Prices and refunds"],
  ["fair-use", "Fair use"],
  ["the-legal-bit", "The legal bit"],
] as const;

function Terms() {
  return (
    <Page>
      <PageHead
        eyebrow="Terms of use · 28 October 2026"
        title="The rules, kept short."
        intro="These terms cover your use of CLOSDD. By creating an account or using the site, you agree to follow them."
      />
      <main className="mx-auto grid w-full max-w-[1216px] gap-12 px-5 py-12 lg:grid-cols-[240px_1fr] lg:px-8 lg:py-16">
        <nav aria-label="On this page" className="h-fit lg:sticky lg:top-6">
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-coral-ink">
            On this page
          </p>
          <ol className="mt-4 space-y-2">
            {sections.map(([id, title]) => (
              <li key={id}>
                <a className="text-[15px] text-ink/70 hover:text-navy" href={`#${id}`}>
                  {title}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="max-w-[780px] space-y-12">
          <section id="the-basics">
            <h2 className="text-2xl font-extrabold text-navy">The basics</h2>
            <p className="mt-4 leading-7 text-ink/70">
              CLOSDD is a platform for salespeople to present their experience and apply for roles,
              and for employers to post roles and review applicants. The service is operated by
              <strong> Closdd Ltd</strong>, company number <strong>[company number]</strong>,
              registered office <strong>167 City Rd, London EC1V 1AW</strong>. You must be at least
              18 and provide accurate information when using the site.
            </p>
          </section>
          <section id="salespeople">
            <h2 className="text-2xl font-extrabold text-navy">Salespeople</h2>
            <p className="mt-4 leading-7 text-ink/70">
              Keep your profile and application information truthful and up to date. You decide
              which roles to apply for and understand that employers who receive your application
              can view its contents, including your pitch and CV if supplied. Creating a profile and
              applying for roles is free.
            </p>
          </section>
          <section id="employers">
            <h2 className="text-2xl font-extrabold text-navy">Employers</h2>
            <p className="mt-4 leading-7 text-ink/70">
              You are responsible for the accuracy and lawfulness of each role you post, and for how
              you use applicant information. Use that information only for evaluating and contacting
              applicants about the role. Do not copy, sell or share candidate data for unrelated
              purposes.
            </p>
          </section>
          <section id="prices-and-refunds">
            <h2 className="text-2xl font-extrabold text-navy">Prices and refunds</h2>
            <p className="mt-4 leading-7 text-ink/70">
              Posting a role is free. A one-off payment of £99 unlocks a role and lets you see every
              applicant’s details. Money back within 7 days if nobody is worth a call: contact us
              within 7 days of payment to request a refund. Any subscription or additional service
              will show its price and terms before you buy it.
            </p>
          </section>
          <section id="fair-use">
            <h2 className="text-2xl font-extrabold text-navy">Fair use</h2>
            <p className="mt-4 leading-7 text-ink/70">
              Do not misuse the site, interfere with its operation, post misleading or unlawful
              content, harass people, or attempt to access accounts or information that are not
              yours. We may suspend access where we reasonably believe these terms have been
              breached.
            </p>
          </section>
          <section id="the-legal-bit">
            <h2 className="text-2xl font-extrabold text-navy">The legal bit</h2>
            <p className="mt-4 leading-7 text-ink/70">
              We provide the platform with reasonable care, but cannot guarantee uninterrupted
              access or that a role will result in a hire. Nothing in these terms limits rights that
              cannot legally be limited. These terms are governed by the laws of England and Wales,
              and disputes are subject to the courts of England and Wales.
            </p>
            <p className="mt-3 leading-7 text-ink/70">
              Questions? Email{" "}
              <a className="underline" href="mailto:info@closdd.com">
                info@closdd.com
              </a>
              .
            </p>
          </section>
          <p className="border-t border-purple-strong/10 pt-5 text-sm text-ink/55">
            Last updated 28 October 2026.
          </p>
        </div>
      </main>
    </Page>
  );
}
